import React, { useEffect, useState, useCallback } from 'react';
import styles from './ToolbarPlugin.module.css';
import CustomSelect from '../../../Select/Select';
import '../../lexicalTheme.css';
import { createPortal } from 'react-dom';
import FloatingLinkEditor from '../../components/FloatingLinkEditor/FloatingLinkEditor';
import FontSizeInput from '../../components/FontsizeInput/FontSizeInput';
import ImagePlugin from '../ImagePlugin/ImagePlugin'

const HeaderToolBar = ({ applyStyleText }) => {
  const HEADING_FORMATS = [
    {
      label: <h1 style={{ fontSize: '36px' }}>Heading 1</h1>,
      value: 36
    },
    {
      label: <h2 style={{ fontSize: '24px' }}>Heading 2</h2>,
      value: 24
    },
    {
      label: <h3 style={{ fontSize: '18px' }}>Heading 3</h3>,
      value: 18
    },
    {
      label: <h4 style={{ fontSize: '14px' }}>Heading 4</h4>,
      value: 14
    },
    {
      label: <h5 style={{ fontSize: '11px' }}>Heading 5</h5>,
      value: 11
    },
    {
      label: <h6 style={{ fontSize: '10px' }}>Heading 6</h6>,
      value: 10
    }
  ];
  const onHeaderStyles = useCallback(
    (value) => {
      applyStyleText({ 'font-size': `${value}px` });
    },
    [applyStyleText]
  );

  return <CustomSelect options={HEADING_FORMATS} onSelect={onHeaderStyles} defaultTitle={'Heading'} />;
};

const TextStyleToolBar = (props) => {
  const {
    isBold,
    isItalic,
    isUnderline,
    isStrikethrough,
    isCode,
    isLink,
    insertLink,
    toggleCommand,
    isSubScript,
    isSuperScript,
    FORMAT_TEXT_COMMAND
  } = props;

  return (
    <div className={styles.textStyleToolBarContainer}>
      <div
        className={`${styles.format} ${isBold ? styles.active : ''}`}
        onClick={() => toggleCommand(FORMAT_TEXT_COMMAND, 'bold')}
      >
        <img src="assets/icons/editor-toolbar/bold.svg" />
      </div>
      <div
        className={`${styles.format} ${isItalic ? styles.active : ''}`}
        onClick={() => toggleCommand(FORMAT_TEXT_COMMAND, 'italic')}
      >
        <img src="assets/icons/editor-toolbar/italic.svg" />
      </div>
      <div
        className={`${styles.format} ${isUnderline ? styles.active : ''}`}
        onClick={() => toggleCommand(FORMAT_TEXT_COMMAND, 'underline')}
      >
        <img src="assets/icons/editor-toolbar/underline.svg" />
      </div>
      <div
        className={`${styles.format} ${isStrikethrough ? styles.active : ''}`}
        onClick={() => toggleCommand(FORMAT_TEXT_COMMAND, 'strikethrough')}
      >
        <img src="assets/icons/editor-toolbar/strike-through.svg" />
      </div>
      <div
        className={`${styles.format} ${isCode ? styles.active : ''}`}
        onClick={() => toggleCommand(FORMAT_TEXT_COMMAND, 'code')}
      >
        <img src="assets/icons/editor-toolbar/code.svg" />
      </div>
      <div
        className={`${styles.format} ${isSuperScript ? styles.active : ''}`}
        onClick={() => toggleCommand(FORMAT_TEXT_COMMAND, 'superscript')}
      >
        <img src="assets/icons/editor-toolbar/superscript.svg" />
      </div>
      <div
        className={`${styles.format} ${isSubScript ? styles.active : ''}`}
        onClick={() => toggleCommand(FORMAT_TEXT_COMMAND, 'subscript')}
      >
        <img src="assets/icons/editor-toolbar/subscript.svg" />
      </div>
    </div>
  );
};

const FontFamilyDropdown = ({ fontFamily, applyStyleText }) => {
  const FONT_STYLE_FORMAT = [
    { label: 'Arial', value: 'Arial' },
    { label: 'Courier New', value: 'Courier New' },
    { label: 'Georgia', value: 'Georgia' },
    { label: 'Times New Roman', value: 'Times New Roman' },
    { label: 'Trebuchet MS', value: 'Trebuchet MS' },
    { label: 'Verdana', value: 'Verdana' },
    { label: 'Impact', value: 'Impact' },
    { label: 'Comic Sans MS', value: 'Comic Sans MS' },
    { label: 'Lucida Console', value: 'Lucida Console' }
  ];
  const onFontFamilySelect = useCallback(
    (value) => {
      applyStyleText({ 'font-family': value });
    },
    [applyStyleText]
  );

  return <CustomSelect options={FONT_STYLE_FORMAT} onSelect={onFontFamilySelect} defaultTitle={fontFamily} defaultValues={FONT_STYLE_FORMAT[0]} type={'FONT_FAMILY'}/>;
};

const FontSizeDropdown = ({ applyStyleText, fontSize }) => {
  const FONT_SIZE_FORMAT = [
    { label: '10', value: 10 },
    { label: '11', value: 11 },
    { label: '12', value: 12 },
    { label: '14', value: 14 },
    { label: '16', value: 16 },
    { label: '18', value: 18 },
    { label: '24', value: 24 },
    { label: '30', value: 30 },
    { label: '36', value: 36 },
    { label: '48', value: 48 },
    { label: '60', value: 60 },
    { label: '72', value: 72 },
    { label: '96', value: 96 }
  ];  

  const onFontFamilySelect = useCallback(
    (value) => {
      applyStyleText({ 'font-size': `${value}px` });
    },
    [applyStyleText]
  );
  return <FontSizeInput options={FONT_SIZE_FORMAT} onSelect={onFontFamilySelect} fontSizeValue={fontSize} />
};

const UndoRedo = ({ editor, REDO_COMMAND, UNDO_COMMAND, canRedo, canUndo }) => {
  return (
    <div className={styles.undoContainer}>
      <button onClick={() => editor.dispatchCommand(UNDO_COMMAND)} disabled={!canUndo}>
        <img src="assets/icons/editor-toolbar/undo.svg" className={styles.format} />
      </button>
      <button onClick={() => editor.dispatchCommand(REDO_COMMAND)} disabled={!canRedo}>
        <img src="assets/icons/editor-toolbar/redo.svg" className={styles.format} />
      </button>
    </div>
  );
};

const TextAlignTool = ({ toggleAlign }) => {
  const ALIGN_FORMATS = [
    {
      icon: 'assets/icons/editor-toolbar/align-left.svg',
      value: 'left',
      label: 'left'
    },
    {
      icon: 'assets/icons/editor-toolbar/align-center.svg',
      value: 'center',
      label: 'center'
    },
    {
      icon: 'assets/icons/editor-toolbar/align-right.svg',
      value: 'right',
      label: 'right'
    },
    {
      icon: 'assets/icons/editor-toolbar/justify.svg',
      value: 'justify',
      label: 'justify'
    }
  ];

  return <CustomSelect options={ALIGN_FORMATS} onSelect={toggleAlign} defaultTitle={'Align'} />;
};

const CodeBlockQuoteTool = (props) => {
  const {
    INDENT_CONTENT_COMMAND,
    OUTDENT_CONTENT_COMMAND,
    toggleCommand,
    formatNumberedList,
    formatBulletList,
    formatQuote,
    formatCode,
    insertLink,
    isLink,
    editor,
    getSelectedNode
  } = props;

  return (
    <div className={styles.textStyleToolBarContainer}>
      <>
        <div className={styles.format} onClick={insertLink}>
          <img src="assets/icons/editor-toolbar/link.svg" />
        </div>
        {isLink &&
          createPortal(
            <FloatingLinkEditor editor={editor} getSelectedNode={getSelectedNode} />,
            document.getElementById('modal-editor')
          )}
      </>
      <div className={styles.format} onClick={formatNumberedList}>
        <img src="assets/icons/editor-toolbar/ol-list.svg" />
      </div>
      <div className={styles.format} onClick={formatBulletList}>
        <img src="assets/icons/editor-toolbar/ul-list.svg" />
      </div>
      <div className={styles.format} onClick={formatQuote}>
        <img src="assets/icons/editor-toolbar/quote.svg" />
      </div>
      <div className={styles.format} onClick={() => toggleCommand(INDENT_CONTENT_COMMAND, undefined)}>
        <img src="assets/icons/editor-toolbar/indent.svg" />
      </div>
      <div className={styles.format} onClick={() => toggleCommand(OUTDENT_CONTENT_COMMAND, undefined)}>
        <img src="assets/icons/editor-toolbar/outdent.svg" />
      </div>
      <div className={styles.format} onClick={formatCode}>
        <img src="assets/icons/editor-toolbar/code-block.svg" />
      </div>
    </div>
  );
};

const CodeLanguages = ({ onCodeLanguageSelect, codeLanguage, codeLanguages }) => {
  const CODE_LANGUAGES =
    codeLanguages?.length > 0
      ? codeLanguages.map((language) => {
          return {
            label: language,
            value: language
          };
        })
      : [];

  return <CustomSelect options={CODE_LANGUAGES} onSelect={onCodeLanguageSelect} defaultTitle={codeLanguage} />;
};

const ImageTool = ({editor}) => {
  return <ImagePlugin editor={editor}/>
}

export {
  HeaderToolBar,
  TextStyleToolBar,
  FontFamilyDropdown,
  FontSizeDropdown,
  UndoRedo,
  TextAlignTool,
  CodeBlockQuoteTool,
  CodeLanguages,
  ImageTool
};
